import React, {type FormEvent, type FunctionComponent, useState} from "react"
import {useDispatch, useSelector} from "react-redux";
import {bindActionCreators} from "@reduxjs/toolkit";
import {actionCreators, type State} from "@App/state";
import {NumberPlateInput} from "@App/Components/NumberPlateInput";
import {OrganizationIsArval} from "@App/utils/conditionals/organization";
import {useTranslation} from "react-i18next";
import {TokenService} from "@App/apis/rest/ayvens/token/tokenService";
import {TokenError} from "@App/apis/rest/ayvens/token/tokenError";
import {DriverDataService} from "@App/apis/rest/ayvens/driverData/driverDataService";
import {chooseService, removePremiumService} from "@App/state/action-creators";

export type VehicleProps = {
    onSubmit: (token: string) => void
}

export const Vehicle: FunctionComponent<VehicleProps> = ({onSubmit}: VehicleProps) => {
    const dispatch = useDispatch();
    const {setNumberPlate, chooseService, removePremiumService, setDriverData} = bindActionCreators(actionCreators, dispatch)
    const userId = useSelector((state: State) => state.auth.userId)
    const numberPlate = useSelector((state: State) => state.orders.numberPlate)
    const [numberPlateError, setNumberPlateError] = useState<string | undefined>(undefined);
    const [token, setToken] = useState<string | undefined>(undefined);
    const driverData = useSelector((state: State) => state.auth.driverData);
    const premiumServices = useSelector((state: State) => state.orders.premiumServices)


    const {t} = useTranslation();

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const tokenService = new TokenService(window.rest_url);
        const driverDataService = new DriverDataService(window.rest_url);
        premiumServices?.forEach((premiumService) => {
            removePremiumService(premiumService)
        })
        chooseService(null);

        try {
            const token = await tokenService.createToken(userId, `${numberPlate.region}-${numberPlate.firstID} ${numberPlate.secondID}`);
            setToken(token)

            const driverData = await driverDataService.getFromJwt(token)
            setDriverData(driverData)

            return null
        } catch (error) {
            if (error instanceof TokenError) {
                // Display a user friendly error message based on error.response.name
                setNumberPlateError(t(`vehicleSelector.validation.errors.error.${error.response.name}`))
            } else {
                // Display a general error message
                setNumberPlateError(t(`vehicleSelector.validation.errors.error.invalid_request`)) // Etwas ist schiefgelaufen
            }
        }
    }

    // TODO maybe we can refactor this page a bit. Currently, there are 2 forms, could be a little bit flaky
    const handleVerification = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        onSubmit(token)
    }

    return (
        <div className={"min-h-screen"}>
            <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-sm">
                    <h2 className="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900">
                        {t('vehicleSelector.title')}
                    </h2>
                </div>

                <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
                    {!driverData && (
                    <form onSubmit={handleSubmit} className="space-y-6" noValidate> {/* Added noValidate to prevent browser default validation popups */}
                        <div>
                            <label htmlFor="email" className="block text-sm/6 font-medium text-gray-900">
                                {t('vehicleSelector.numberPlate')}
                            </label>
                            <div className="mt-2">
                                <NumberPlateInput
                                    value={numberPlate}
                                    onChange={setNumberPlate}
                                    showNumberPlateSyncMessage={OrganizationIsArval(organization)}
                                />
                            </div>

                            {numberPlateError && (
                                <p id="number-plate-error" className="mt-2 text-sm text-error-600" role="alert">
                                    {numberPlateError}
                                </p>
                            )}
                        </div>
                        <div>
                            <button
                                type="submit"
                                data-testid="submit"
                                className="flex w-full justify-center rounded-md bg-secondary px-3 py-1.5 text-sm/6 font-semibold text-white shadow-sm hover:bg-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                            >
                                {t('vehicleSelector.searchNumberPlate')}
                            </button>
                        </div>
                    </form>
                    )}
                    {driverData && (
                        <form onSubmit={handleVerification} className="space-y-6" noValidate> {/* Added noValidate to prevent browser default validation popups */}
                            <div>
                                <div className="mt-2">
                                    <div className="text-l">{ t('vehicleSelector.isRightVehicle') }</div>
                                </div>
                                <div className="mt-2">
                                    <div className="rounded-md bg-yellow-50 p-4">
                                        <div className="flex">
                                            <div className="ml-3">
                                                <h3 className="text-sm font-medium text-yellow-800">
                                                    {`${driverData.keyNumber.type.generation.brand.name} ${driverData.keyNumber.type.generation.name} ${driverData.keyNumber.type.name}`}
                                                </h3>
                                                <div className="mt-2 text-sm text-yellow-700">
                                                    <p>
                                                        {`${t('vehicleSelector.numberPlate')}: ${driverData.vehicle.numberPlate}`}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className={"grid grid-cols-2 gap-4"}>
                                <button
                                    type="button"
                                    onClick={() => setDriverData(null)}
                                    className="flex w-full justify-center rounded-md bg-secondary px-3 py-1.5 text-sm/6 font-semibold text-white shadow-sm hover:bg-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                                >
                                    {t('vehicleSelector.backToNumberPlateSelection')}
                                </button>
                                <button
                                    type="submit"
                                    data-testid="submit"
                                    className="flex w-full justify-center rounded-md bg-secondary px-3 py-1.5 text-sm/6 font-semibold text-white shadow-sm hover:bg-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                                >
                                    {t('vehicleSelector.submit')}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
